<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-1">
        <i class="far fa-box"></i> Glass Blocks
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-1">

        <DataTable
          ref="blockDataTable"
          :columns="columnList"
          defaultColumnOrderDir="DESC"
          :defaultColumnOrderIndex="0"
          :url="dataURL"
          :filterOptions="filterOptions"
          :locationFilterOptions="locationFilterOptions"
          :displayLocationFilter="true"
          :displayTools="true"
          :hasDateRangeFilter="true"
          :displayExportTools="true"
          :displayActions="true"
          exportTitle="Glass Blocks"
        >
          <template v-slot:title>
            <h3>Glass Block List</h3>
          </template>
          <template v-slot:tools>
            <ActionButton type="new" v-if="canCreateBlock" sizeClass="btn" @click="showCreateBlock"> New Block</ActionButton>
          </template>
          <template v-slot:actionList>
            <li><a href="#" @click.prevent="printFilteredGlassBlocks" class="dropdown-menu-item-icon"><i class="far fa-print"></i> Print Filtered Block Labels</a></li>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td>
                {{row.data.barcode}}
              </td>
              <td>
                <LabelBadge class="label-glassblock-opened" v-if="row.data.isOpened">Opened</LabelBadge>
                <LabelBadge class="label-glassblock-closed" v-if="!row.data.isOpened">Available</LabelBadge>
              </td>
              <td>
                {{row.data.height}} x {{row.data.width}}
              </td>
              <td>
                {{row.data.sheetQuantity}}
              </td>
              <td>
                {{formatHyphenEmptyValue(row.data.referenceCode)}}
              </td>
              <td>
                <LinkStock :id="row.data.stock.id" :name="row.data.stock.name" />
              </td>
              <td>
                {{row.data.inventoryBin ? row.data.inventoryBin.code : '-'}}
              </td>
              <td>
                <LabelAccountLocationBadge :accountLocationId="row.data.inventoryBin ? row.data.inventoryBin.accountLocation.id : null" />
              </td>
              <td>
                <LinkContact :id="row.data.contact.id" :name="row.data.contact.name" />
              </td>
              <td>
                <LinkPurchaseOrder v-if="glassBlockPurchaseOrderId(row.data)" :id="glassBlockPurchaseOrderId(row.data)" :name="glassBlockPurchaseOrderTitle(row.data)" />
                <span v-else>-</span>
              </td>
              <td>
                <LinkOrder v-if="glassBlockSalesOrderId(row.data)" :id="glassBlockSalesOrderId(row.data)" :name="glassBlockSalesOrderTitle(row.data)" />
                <span v-else>-</span>
              </td>
              <td>
                {{formatDateTime(row.data.createdAt)}}
              </td>
              <td>
                <span v-if="row.data.isOpened">{{formatHyphenEmptyValue(formatDateTime(row.data.openedDate))}}</span>
                <span v-else>-</span>
              </td>
              <td>
                <ActionDropMenu :tableAction="true" :forceDropdown="true">
                  <DropdownMenuItem v-if="!row.data.isOpened && canEditBlock" @click="editBlock(row.data)" icon="fa-pencil">Edit Block</DropdownMenuItem>
                  <DropdownMenuItem v-if="!row.data.isOpened && canOpenBlock" @click="openBlock(row.data)" icon="fa-box-open">Open Block</DropdownMenuItem>
                  <DropdownMenuItem v-if="row.data.isOpened && canCloseBlock" @click="closeBlock(row.data)" icon="fa-box">Close Block</DropdownMenuItem>
                  <DropdownMenuDivider v-if="!row.data.isOpened && canOpenBlock" />
                  <DropdownMenuItem @click="printGlassBlockLabel(row.data)" v-if="!row.data.isOpened" icon="fa-print">Print Block Label</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </tr>
          </template>
        </DataTable>

      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import TabbedBox from "@/components/layout/tabbedBox.vue"
import Tab from "@/components/layout/tab.vue"
import TabPage from "@/components/layout/tabPage.vue"

import GlassBlockAddDialog from '@/modules/inventory/views/glassBlockAddDialog'
import GlassBlockEditDialog from '@/modules/inventory/views/glassBlockEditDialog'

import { InventoryStockTypes, InventoryStockTypeLookup } from '@/store/types'

export default {
  name: 'GlassBlockList',
  components: {
    TabbedBox,
    Tab,
    TabPage
  },
  props: {
    filterType: String
  },
  created() {
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
  },
  computed: {
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
      }
      return '/inventory/glassblocks?' + $.param(params)
    },
    filterOptions() {
      var filterList = [{
        field: 'isOpened',
        name: 'Status',
        options: [
          { name: 'Opened', value: 1, class: 'label-glassblock-opened' },
          { name: 'Available', value: 0, class: 'label-glassblock-closed' },
        ]
      },
      {
        field: '$sourceTransaction.id$',
        name: 'Other',
        operator: 'ne',
        options: [
          { name: 'Sales Order', value: null, class: 'label-order-status-completed' },
        ]
      }]

      return filterList
    },
    locationFilterOptions() {
      var locationOptions = (this.accountLocations || []).filter(location => location.isActive).map(location => {
        return {
          name: location.name,
          value: location.id,
          colour: location.highlightColour,
        }
      })
      
      return [{
        field: '$inventoryBin.accountLocation.id$',
        name: 'Location',
        options: locationOptions
      }]
    },
    canOpenBlock() {
      return this.userHasUpdatePermission(this.PermissionTypes.InventoryPermissionTypes.stock)
    },
    canCloseBlock() {
      return this.userHasUpdatePermission(this.PermissionTypes.InventoryPermissionTypes.stock)
    },
    canEditBlock() {
      return this.userHasCreatePermission(this.PermissionTypes.InventoryPermissionTypes.stockTransaction)
    },
    canCreateBlock() {
      return this.userHasCreatePermission(this.PermissionTypes.InventoryPermissionTypes.stockTransaction)
    }
  },
  data: () => {
    return {
      accountLocations: [],
      columnList: [
        { title: "Glass Block", data: "id" },
        { title: "Status", data: "isOpened" },
        { title: "Size", data: "size", sortable: false },
        { title: "Sheets", data: "sheetQuantity" },
        { title: "Reference", data: "referenceCode" },
        { title: "Stock", data: "stock.name" },
        { title: "Bin", data: "inventoryBin.code" },
        { title: "Location", data: "inventoryBin.accountLocation.name" },
        { title: "Supplier", data: "contact.name" },
        { title: "Purchase Order", data: "purchaseOrderReceivedItems.purchaseOrder.sequenceNumber" },
        { title: 'Sales Order', data: 'sourceTransaction.order.sequenceNumber' },
        { title: "Creation Date", data: "createdAt", dateFilter: true, type: 'datetime' },
        { title: "Opened Date", data: "openedDate", dateFilter: true, type: 'datetime' },
        { title: "Actions", data: "", sortable: false, export: false }
      ]
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      Promise.all([
        this.$apiService.adminService.getAccountLocations()
      ])
      .then(results => {
        this.accountLocations = results[0].data.filter(location => location.isActive)
      })
    },

    glassBlockPurchaseOrderId(glassBlock) {
      if(glassBlock.purchaseOrderReceivedItems) {
        if(glassBlock.purchaseOrderReceivedItems[0] && glassBlock.purchaseOrderReceivedItems[0].purchaseOrder)
          return glassBlock.purchaseOrderReceivedItems[0].purchaseOrder.id
      }

      return null
    },
    glassBlockPurchaseOrderTitle(glassBlock) {
      if(glassBlock.purchaseOrderReceivedItems) {
        if(glassBlock.purchaseOrderReceivedItems[0] && glassBlock.purchaseOrderReceivedItems[0].purchaseOrder)
          return glassBlock.purchaseOrderReceivedItems[0].purchaseOrder.sequenceNumber
      }

      return null
    },
    glassBlockSalesOrderId(glassBlock) {
      if(glassBlock.sourceTransaction) {
        if(glassBlock.sourceTransaction[0] && glassBlock.sourceTransaction[0].order)
          return glassBlock.sourceTransaction[0].order.id
      }

      return null
    },
    glassBlockSalesOrderTitle(glassBlock) {
      if(glassBlock.sourceTransaction) {
        if(glassBlock.sourceTransaction[0] && glassBlock.sourceTransaction[0].order)
          return glassBlock.sourceTransaction[0].order.sequenceNumber
      }

      return null
    },
    printGlassBlockLabel(glassBlock) {
      this.$apiService.inventoryService.generatePrintBlockLabelURL(glassBlock.id, { download: true, label: true, blocklabel: true })
      .then(response => {
        window.location.href = response.data.url
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Print Error')
      })
    },
    openBlock(glassBlock) {
      this.confirmMessage('Open Block?', 'Open the block? This will convert the block to loose sheets and will mark the block as opened')
      .then(confirm => {
        if(confirm) {
          this.$apiService.inventoryService.openGlassBlock(glassBlock.id)
          .then(response => {
            this.successToast('Block Opened')
            this.$refs.blockDataTable.reload()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Could not open the block')
          })
        }
      })
    },
    closeBlock(glassBlock) {
      this.confirmMessage('Close Block?', 'Close the block? This will mark the block as closed and return it back into inventory')
      .then(confirm => {
        if(confirm) {
          this.$apiService.inventoryService.closeGlassBlock(glassBlock.id)
          .then(response => {
            this.successToast('Block Closed')
            this.$refs.blockDataTable.reload()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Could not close the block')
          })
        }
      })
    },
    editBlock(glassBlock) {
      this.displayModal(GlassBlockEditDialog, {
        glassBlockId: glassBlock.id
      })
      .then((success) => {
        if(success) {
          this.successToast('Block Updated')
          this.$refs.blockDataTable.reload()
        }
      })
    },
    showCreateBlock() {
      // Display add offcut dialog
      this.displayModal(GlassBlockAddDialog, { 
      })
      .then((success) => {
        if(success) {
          this.successToast('Block Created')
          this.$refs.blockDataTable.reload()
        }
      })
    },

    printFilteredGlassBlocks() {
      this.displayProcessingDialog({
        title: 'Getting Blocks',
        message: 'Downloading block list for printing. Please wait...',
        icon: 'fa-sync',
        onBeforeOpen: (instance) => {
          let dataTable = this.$refs.blockDataTable

          dataTable.downloadRowData({ ignorePagination: true })
          .then(blockList => {
            instance.hide()

            // Filter out opened blocks
            // Server won't print them anyway
            blockList = blockList.filter(block => !block.isOpened)

            this.$apiService.inventoryService.generatePrintBlockLabelListURL(blockList.map(block => parseInt(block.id)), { 
              download: true, 
              label: true, 
              blocklabel: true
            })
            .then(response => {
              window.location.href = response.data.url
            })
            .catch(err => {
              console.error(err)
              this.displayNetworkError(err, 'Print Error')
            })
          })
          .catch(err => {
            console.error(err)
            this.displayNetworkError(err, 'Error', 'Could not download block data for printing')
          })
          .finally(() => {
            instance.hide()
          })
          
        }
      })

      // TODO: Get all blocks in the filtered list

      // TODO: Filter out opened blocks

      // TODO: Display dialog / msgbox with count of blocks to print and confirm printing

    }

  }
};
</script>
